import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
    Avatar,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    OutlinedInput,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React from 'react';

// ── Filter options ─────────────────────────────────────────────────────────
const SOURCE_OPTIONS = [
    { _id: 'quickRequest', label: 'Direct' },
    { _id: 'appointment', label: 'Appointment' },
    { _id: 'invoice', label: 'Invoice' },
];

const DELIVERY_METHOD_OPTIONS = [
    { _id: 'text', label: 'Text' },
    { _id: 'email', label: 'Email' },
    { _id: 'app', label: 'App' },
];

const TYPE_OPTIONS = [
    { _id: 'apex_auto_app', label: 'Apex Auto' },
    { _id: 'walk_in', label: 'Walk In' },
    { _id: 'shop_created', label: 'Shop Created' },
];

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
    menuPadding: { padding: '0', width: 'calc(100% + 10px)!important' },
    formControlLabel: { padding: '6px 16px', marginRight: '0!important', width: '100%' },
    filterBtn: (isActive) => ({
        color: isActive ? 'white!important' : 'black!important',
        fontWeight: 500,
        lineHeight: '24px',
        padding: '6px 12px!important',
        height: '40px',
    }),
    countAvatar: {
        bgcolor: 'white',
        color: 'primary.main',
        fontSize: '14px!important',
        fontWeight: '500!important',
        width: 24,
        height: 24,
    },
    clearBtn: {
        background: '#0000001A',
        color: '#000000',
        boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.25)',
        '&:hover': { background: '#0000001A', boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.25)' },
        fontWeight: 500,
        lineHeight: '24px',
    },
    datePicker: { width: 240, minWidth: 240, maxWidth: 240 },
    datePickerTextField: { sx: { width: 240, minWidth: 240, maxWidth: 240 }, inputProps: { id: 'review-date-input' } },
    loaderContainer: { py: 2 },
};

// ── Reusable multi-select filter ───────────────────────────────────────────
const MultiSelectFilter = ({ label, options = [], selected = [], onChange, anchorEl, onOpen, onClose, loading }) => {
    const open = Boolean(anchorEl);
    const isActive = selected.length > 0;

    return (
        <>
            <Button
                variant={isActive ? 'contained' : 'text'}
                color="primary"
                onClick={onOpen}
                sx={styles.filterBtn(isActive)}
                startIcon={isActive && <Avatar sx={styles.countAvatar}>{selected.length}</Avatar>}
                endIcon={<ExpandMoreIcon />}
            >
                {label}
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={onClose} PaperProps={{ style: { maxHeight: 400, minWidth: 180 } }}>
                {loading ? (
                    <MenuItem>
                        <Grid container justifyContent="center" alignItems="center" sx={styles.loaderContainer}>
                            <CircularProgress size={24} />
                        </Grid>
                    </MenuItem>
                ) : options.length === 0 ? (
                    <MenuItem disabled>No options</MenuItem>
                ) : (
                    options.map((opt) => (
                        <MenuItem key={opt._id} sx={styles.menuPadding}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selected.includes(opt._id)}
                                        onChange={() => {
                                            const updated = selected.includes(opt._id) ? selected.filter((s) => s !== opt._id) : [...selected, opt._id];
                                            onChange(updated);
                                        }}
                                    />
                                }
                                label={opt.label}
                                sx={styles.formControlLabel}
                            />
                        </MenuItem>
                    ))
                )}
            </Menu>
        </>
    );
};

// ── Main filter bar ────────────────────────────────────────────────────────
const ReviewRequestFilters = ({
    selectDate,
    onDateChange,
    selectedSources,
    onSourcesChange,
    selectedDeliveryMethods,
    onDeliveryMethodsChange,
    selectedTypes,
    onTypesChange,
    search,
    onSearchChange,
    hasClearableFilters,
    clearFilterLabel,
    onClearFilters,
}) => {
    const [sourceAnchorEl, setSourceAnchorEl] = React.useState(null);
    const [deliveryAnchorEl, setDeliveryAnchorEl] = React.useState(null);
    const [typeAnchorEl, setTypeAnchorEl] = React.useState(null);

    return (
        <Grid container marginBottom={'24px'} alignItems={'center'} justifyContent={'space-between'}>
            {/* ── Left: filter controls ── */}
            <Grid container item xs={12} sm={8} md={8} justifyContent={'flex-start'} alignItems={'center'}>
                {/* Date Picker */}
                <Grid item xs="auto" padding={'6px'}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Select Date"
                            value={selectDate}
                            onChange={onDateChange}
                            sx={styles.datePicker}
                            slotProps={{
                                textField: styles.datePickerTextField,
                                field: { clearable: true },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                {/* Source */}
                <Grid item xs="auto" padding={'6px'}>
                    <MultiSelectFilter
                        label="SOURCE"
                        options={SOURCE_OPTIONS}
                        selected={selectedSources}
                        onChange={onSourcesChange}
                        anchorEl={sourceAnchorEl}
                        onOpen={(e) => setSourceAnchorEl(e.currentTarget)}
                        onClose={() => setSourceAnchorEl(null)}
                    />
                </Grid>

                {/* Delivery Method */}
                <Grid item xs="auto" padding={'6px'}>
                    <MultiSelectFilter
                        label="DELIVERY METHOD"
                        options={DELIVERY_METHOD_OPTIONS}
                        selected={selectedDeliveryMethods}
                        onChange={onDeliveryMethodsChange}
                        anchorEl={deliveryAnchorEl}
                        onOpen={(e) => setDeliveryAnchorEl(e.currentTarget)}
                        onClose={() => setDeliveryAnchorEl(null)}
                    />
                </Grid>

                {/* Type */}
                <Grid item xs="auto" padding={'6px'}>
                    <MultiSelectFilter
                        label="TYPE"
                        options={TYPE_OPTIONS}
                        selected={selectedTypes}
                        onChange={onTypesChange}
                        anchorEl={typeAnchorEl}
                        onOpen={(e) => setTypeAnchorEl(e.currentTarget)}
                        onClose={() => setTypeAnchorEl(null)}
                    />
                </Grid>

                {/* Clear Filters */}
                {hasClearableFilters && (
                    <Grid item xs="auto" padding={'6px'}>
                        <Button variant="contained" startIcon={<Close />} onClick={onClearFilters} sx={styles.clearBtn}>
                            {clearFilterLabel}
                        </Button>
                    </Grid>
                )}
            </Grid>

            {/* ── Right: Search ── */}
            <Grid container item xs={12} sm={4} md={4} justifyContent={'flex-end'} padding={'6px'}>
                <FormControl fullWidth>
                    <InputLabel htmlFor="review-search">Search</InputLabel>
                    <OutlinedInput
                        id="review-search"
                        placeholder="Customer name, phone, email etc..."
                        startAdornment={
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        }
                        value={search}
                        label="Search"
                        onChange={(e) => onSearchChange(e.target.value)}
                    />
                </FormControl>
            </Grid>
        </Grid>
    );
};

export default ReviewRequestFilters;
